import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useLanguage from "../hooks/useLanguage";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  CircularProgress,
  Container,
  Divider,
  Drawer,
  List,
  ListItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import FemaleIcon from "@mui/icons-material/Female";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import MaleIcon from "@mui/icons-material/Male";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SwapVertRoundedIcon from "@mui/icons-material/SwapVertRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import { useTheme } from "@mui/material/styles";
import TopNavBar from "../components/TopNavBar";
import { useAuth } from "../context/AuthContext";
import { getAllJobs, updateJob } from "../api/jobs";
import {
  getRoomRestrictionType,
  getRoomRestrictionColor,
} from "../utils/roomRestriction";
import fieldServiceTranslations from "../assets/variables/fieldServiceTranslations";

// Key used to persist the user's manual job ordering across page reloads.
const JOB_ORDER_STORAGE_KEY = "myServices.jobOrder";

const loadStoredJobOrder = () => {
  try {
    const raw = window.localStorage.getItem(JOB_ORDER_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const toText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getStatusColor = (status) => {
  switch (toText(status)) {
    case "pending":
      return "warning";
    case "assigned":
      return "info";
    case "in_progress":
    case "in progress":
      return "primary";
    case "completed":
      return "success";
    case "failed":
    case "cancelled":
      return "error";
    case "not_applicable":
    case "not applicable":
      return "default";
    default:
      return "default";
  }
};

const toDateBucket = (dateString) => {
  if (!dateString) return "other";

  const value = new Date(dateString);
  if (Number.isNaN(value.getTime())) return "other";

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const key = value.toDateString();
  if (key === yesterday.toDateString()) return "yesterday";
  if (key === today.toDateString()) return "today";
  if (key === tomorrow.toDateString()) return "tomorrow";
  return "other";
};

const getUserIdCandidates = (user) =>
  [user?.pk_id, user?.id, user?.userId]
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map((value) => String(value));

const normalizePriority = (priority) =>
  String(priority || "")
    .trim()
    .toLowerCase();

const isHighPriority = (priority) =>
  normalizePriority(priority) === "high" ||
  normalizePriority(priority) === "urgent";

const isCurrentUserJob = (job, user, role) => {
  const userIds = getUserIdCandidates(user);
  const assignedId = String(job?.assigned_technician_id || "");
  const assignedName = toText(job?.assigned_technician_name);
  const username = toText(user?.username || user?.uk_username || user?.email);

  if (assignedId && userIds.includes(assignedId)) {
    return true;
  }

  if (assignedName && username && assignedName === username) {
    return true;
  }

  return false;
};

// Hide jobs whose route/assignment period hasn't been finalized or was cancelled.
const isVisibleAssignmentPeriodStatus = (job) => {
  const status = toText(job?.assignment_period_status);
  return status !== "draft" && status !== "cancelled";
};

const normalizeTaskStatus = (status) => {
  const normalized = toText(status);
  if (!normalized) return "pending";
  if (normalized === "in progress") return "in_progress";
  return normalized;
};

export const getDefaultTaskStatusFilter = (jobStatus) => {
  const normalized = normalizeTaskStatus(jobStatus);
  return [
    "completed",
    "failed",
    "cancelled",
    "skipped",
    "not_applicable",
  ].includes(normalized)
    ? "finished"
    : "todo";
};

export const resolveNextJobStatus = (currentStatus, allDone) => {
  const normalizedCurrentStatus = normalizeTaskStatus(currentStatus);
  if (
    ["completed", "failed", "cancelled", "skipped", "not_applicable"].includes(
      normalizedCurrentStatus,
    )
  ) {
    return normalizedCurrentStatus;
  }

  if (allDone) {
    return "completed";
  }

  return "in_progress";
};

const isTerminalTaskStatus = (status) => {
  const normalized = normalizeTaskStatus(status);
  return [
    "completed",
    "skipped",
    "cancelled",
    "failed",
    "not_applicable",
  ].includes(normalized);
};

const isTerminalJobStatus = (status) => {
  const normalized = normalizeTaskStatus(status);
  return [
    "completed",
    "failed",
    "cancelled",
    "skipped",
    "not_applicable",
  ].includes(normalized);
};

const getTaskTitle = (task, index, language) => {
  if (language === "da" && task?.task_name_dk) return task.task_name_dk;
  return (
    task?.title || task?.task_name || task?.task_label || `Task ${index + 1}`
  );
};

const getTaskStableKey = (task, index) =>
  String(
    task?.service_task_id ||
      task?.pk_id ||
      `${task?.task_id || "task"}_${index}`,
  );

const formatTaskStatusLabel = (status) =>
  normalizeTaskStatus(status).replaceAll("_", " ");

const shouldPromptFailureReason = (status) =>
  ["failed", "skipped", "not_applicable"].includes(normalizeTaskStatus(status));

const isNonRoutineTask = (task) => {
  const value = task?.is_non_routine_task;
  return value === true || value === 1 || value === "1";
};

const getRoomNameColor = (restriction) => getRoomRestrictionColor(restriction);

const RoomRestrictionBadge = ({ restriction }) => {
  const type = getRoomRestrictionType(restriction);
  if (!type) return null;

  const GenderIcon = type === "male" ? MaleIcon : FemaleIcon;

  return (
    <Box
      component="span"
      aria-label={`${type} room restriction`}
      title={`${type} room restriction`}
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 36,
        height: 36,
        flexShrink: 0,
      }}
    >
      <SmartToyOutlinedIcon sx={{ fontSize: 32, color: "text.secondary" }} />
      <GenderIcon
        sx={{
          position: "absolute",
          right: -2,
          bottom: -2,
          fontSize: 18,
          color: type === "male" ? "info.main" : "secondary.main",
          bgcolor: "background.paper",
          borderRadius: "50%",
        }}
      />
    </Box>
  );
};

// MySQL DATETIME columns are returned without a timezone indicator.
// Append 'Z' so the browser treats them as UTC instead of local time.
const toUtcDate = (value) => {
  if (!value) return null;
  const str = String(value).trim();
  if (!str) return null;
  const hasOffset = /[Zz]|[+-]\d{2}:?\d{2}$/.test(str);
  const normalized = hasOffset ? str : str.replace(" ", "T") + "Z";
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTime = (value) => {
  const d = toUtcDate(value);
  if (!d) return "-";
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// My Services only needs yesterday/today/tomorrow's jobs.
const getMyServicesFetchWindow = () => {
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - 1);
  const end = new Date(today);
  end.setDate(end.getDate() + 1);

  return { startDate: toDateKey(start), endDate: toDateKey(end) };
};

const formatTimeLabel = (value) => {
  const d = toUtcDate(value);
  if (!d) return "-";
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDuration = (minutes) => {
  if (minutes == null || minutes < 0) return null;
  if (minutes === 0) return "< 1 min";
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
};

const formatPartsUsedForInput = (partsUsed) => {
  if (!partsUsed) return "";

  if (Array.isArray(partsUsed)) {
    return partsUsed.join("\n");
  }

  if (typeof partsUsed === "string") {
    try {
      const parsed = JSON.parse(partsUsed);
      if (Array.isArray(parsed)) {
        return parsed.join("\n");
      }
      return String(partsUsed);
    } catch (_) {
      return String(partsUsed);
    }
  }

  return "";
};

const parsePartsUsedInput = (partsUsedText) => {
  const cleaned = String(partsUsedText || "").trim();
  if (!cleaned) return null;

  const parts = cleaned
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);

  return parts.length > 0 ? parts : null;
};

const calculateTimeSpentMinutes = (startedAt, completedAt) => {
  if (!startedAt || !completedAt) return null;

  const started = new Date(startedAt);
  const completed = new Date(completedAt);
  if (Number.isNaN(started.getTime()) || Number.isNaN(completed.getTime())) {
    return null;
  }

  const diffMs = completed.getTime() - started.getTime();
  if (diffMs < 0) return null;
  return Math.round(diffMs / 60000);
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Set to false to hide the per-task started/completed time chips
const SHOW_TASK_TIME_CHIPS = false;
const AUTO_FAIL_PENDING_TASK_REASON = "Failed because job was failed";

export default function ServicesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { user } = useAuth();
  const username =
    user?.username || user?.uk_username || user?.email || "Unknown";
  const role = toText(user?.role);

  const [language, setLanguage] = useLanguage();

  const t = (key) => {
    const keys = key.split(".");
    let value = fieldServiceTranslations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [panelError, setPanelError] = useState("");
  const [jobs, setJobs] = useState([]);
  // jobOrder maps a day-tab bucket ("yesterday" | "today" | "tomorrow") to an
  // ordered array of job pk_ids reflecting the user's manual ordering for
  // that day. Lazily initialized from localStorage so it survives reloads.
  const [jobOrder, setJobOrder] = useState(loadStoredJobOrder);
  // jobOrderToggle switches the job list into "reorder mode": tapping a job
  // picks it, tapping a second job swaps the two positions.
  const [jobOrderToggle, setJobOrderToggle] = useState(false);
  const [jobOrderSelectedId, setJobOrderSelectedId] = useState(null);
  const [dayTab, setDayTab] = useState("today");
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedTaskKeys, setExpandedTaskKeys] = useState({});
  const [taskDrafts, setTaskDrafts] = useState({});
  const [taskStatusFilter, setTaskStatusFilter] = useState("todo");
  const [animatingTaskKeys, setAnimatingTaskKeys] = useState({});
  const [taskFailureHighlightKeys, setTaskFailureHighlightKeys] = useState({});
  const [pendingTaskStatusByKey, setPendingTaskStatusByKey] = useState({});
  const [pendingFailureFocusTaskKey, setPendingFailureFocusTaskKey] =
    useState(null);
  const [showFailJobInline, setShowFailJobInline] = useState(false);
  const [failJobReasonTouched, setFailJobReasonTouched] = useState(false);
  const [failJobReason, setFailJobReason] = useState("");
  // Live seconds elapsed since job was started — updates every second
  const [jobElapsedSeconds, setJobElapsedSeconds] = useState(0);
  const jobTimerRef = useRef(null);
  const failureInputRefs = useRef({});
  const taskListScrollRef = useRef(null);
  const drawerPullStartYRef = useRef(null);
  const drawerPullDistanceRef = useRef(0);

  const loadFieldData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const jobsResponse = await getAllJobs(getMyServicesFetchWindow());

      const allJobs = Array.isArray(jobsResponse)
        ? jobsResponse
        : Array.isArray(jobsResponse?.jobs)
          ? jobsResponse.jobs
          : Array.isArray(jobsResponse?.data)
            ? jobsResponse.data
            : [];
      console.log(allJobs);

      const myJobs = allJobs.filter(
        (job) =>
          isCurrentUserJob(job, user, role) &&
          isVisibleAssignmentPeriodStatus(job),
      );
      setJobs(myJobs);
    } catch (err) {
      setError(err?.message || "Failed to load your field services");
    } finally {
      setLoading(false);
    }
  }, [role, user]);

  useEffect(() => {
    loadFieldData();
  }, [loadFieldData]);

  // Persist manual job ordering so it survives a page refresh.
  useEffect(() => {
    try {
      window.localStorage.setItem(
        JOB_ORDER_STORAGE_KEY,
        JSON.stringify(jobOrder),
      );
    } catch {
      // Ignore write failures (e.g. private browsing / storage full).
    }
  }, [jobOrder]);

  // Reset any in-progress swap selection whenever reorder mode is turned
  // off or the visible day tab changes.
  useEffect(() => {
    setJobOrderSelectedId(null);
  }, [jobOrderToggle, dayTab]);

  useEffect(() => {
    if (!drawerOpen || !isMobile) return undefined;

    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverscrollY = html.style.overscrollBehaviorY;
    const prevBodyOverscrollY = body.style.overscrollBehaviorY;

    html.style.overscrollBehaviorY = "none";
    body.style.overscrollBehaviorY = "none";

    return () => {
      html.style.overscrollBehaviorY = prevHtmlOverscrollY;
      body.style.overscrollBehaviorY = prevBodyOverscrollY;
    };
  }, [drawerOpen, isMobile]);

  const handleDrawerPullStart = useCallback(
    (event) => {
      if (!isMobile) return;
      const y = event.touches?.[0]?.clientY;
      drawerPullStartYRef.current = typeof y === "number" ? y : null;
      drawerPullDistanceRef.current = 0;
    },
    [isMobile],
  );

  const handleDrawerPullMove = useCallback(
    (event) => {
      if (!isMobile || drawerPullStartYRef.current == null) return;
      const y = event.touches?.[0]?.clientY;
      if (typeof y !== "number") return;

      const delta = y - drawerPullStartYRef.current;
      if (delta > 0) {
        drawerPullDistanceRef.current = delta;
        if (event.cancelable) {
          event.preventDefault();
        }
      }
    },
    [isMobile],
  );

  const handleDrawerPullEnd = useCallback(() => {
    if (!isMobile) {
      drawerPullStartYRef.current = null;
      drawerPullDistanceRef.current = 0;
      return;
    }

    const pulledDistance = drawerPullDistanceRef.current;
    drawerPullStartYRef.current = null;
    drawerPullDistanceRef.current = 0;

    if (pulledDistance > 72) {
      setDrawerOpen(false);
    }
  }, [isMobile]);

  const closeDrawerAfterTerminalUpdate = useCallback(() => {
    setSelectedJobId(null);
    setDrawerOpen(false);
  }, []);

  const dayFilteredJobs = useMemo(() => {
    const rows = jobs.filter((job) => {
      const sourceDate = job?.scheduled_start || job?.created_at;
      return toDateBucket(sourceDate) === dayTab;
    });

    rows.sort((a, b) => {
      const aTerminal = isTerminalJobStatus(a?.status) ? 1 : 0;
      const bTerminal = isTerminalJobStatus(b?.status) ? 1 : 0;
      if (aTerminal !== bTerminal) {
        return aTerminal - bTerminal;
      }

      const aHigh = isHighPriority(a?.priority);
      const bHigh = isHighPriority(b?.priority);
      if (aHigh !== bHigh) {
        return aHigh ? -1 : 1;
      }

      const aTime =
        toUtcDate(a?.scheduled_start || a?.created_at)?.getTime() || 0;
      const bTime =
        toUtcDate(b?.scheduled_start || b?.created_at)?.getTime() || 0;
      return aTime - bTime;
    });

    // Completed jobs always sink to the bottom of the list, even if a
    // manual order (below) would otherwise place them elsewhere. Relative
    // order within each group (active / completed) is preserved.
    const sinkCompleted = (list) => {
      const active = [];
      const completed = [];
      list.forEach((job) => {
        if (toText(job?.status) === "completed") {
          completed.push(job);
        } else {
          active.push(job);
        }
      });
      return [...active, ...completed];
    };

    // Apply the user's manual order for this day, if one has been saved.
    // Jobs that aren't in the saved order yet (e.g. newly assigned jobs)
    // fall back to the default sort above and are appended in that order.
    const savedOrder = jobOrder[dayTab];
    if (Array.isArray(savedOrder) && savedOrder.length > 0) {
      const remaining = new Map(rows.map((job) => [String(job.pk_id), job]));
      const ordered = [];

      savedOrder.forEach((id) => {
        const key = String(id);
        const job = remaining.get(key);
        if (job) {
          ordered.push(job);
          remaining.delete(key);
        }
      });

      // Append anything left over (new jobs not yet part of the saved
      // order) preserving the default sort among themselves.
      remaining.forEach((job) => ordered.push(job));

      return sinkCompleted(ordered);
    }

    return sinkCompleted(rows);
  }, [dayTab, jobs, jobOrder]);

  // Swap two jobs' positions within the current day tab and persist the
  // resulting order. The first tap in reorder mode picks a job; the second
  // tap on a different job swaps it with the first, then clears selection.
  const handleJobOrderSelect = useCallback(
    (jobId) => {
      const key = String(jobId);
      // Completed jobs always sink to the bottom of the list, so picking
      // one to reorder would be a no-op that immediately snaps back.
      const tappedJob = dayFilteredJobs.find(
        (job) => String(job.pk_id) === key,
      );
      if (tappedJob && toText(tappedJob.status) === "completed") {
        return;
      }

      if (!jobOrderSelectedId) {
        setJobOrderSelectedId(key);
        return;
      }

      if (jobOrderSelectedId === key) {
        setJobOrderSelectedId(null);
        return;
      }

      setJobOrder((prevOrder) => {
        const baseline = (
          Array.isArray(prevOrder[dayTab]) && prevOrder[dayTab].length > 0
            ? prevOrder[dayTab]
            : dayFilteredJobs.map((job) => String(job.pk_id))
        ).slice();

        const indexA = baseline.indexOf(jobOrderSelectedId);
        const indexB = baseline.indexOf(key);
        if (indexA === -1 || indexB === -1) return prevOrder;

        [baseline[indexA], baseline[indexB]] = [
          baseline[indexB],
          baseline[indexA],
        ];

        return { ...prevOrder, [dayTab]: baseline };
      });

      setJobOrderSelectedId(null);
    },
    [dayTab, dayFilteredJobs, jobOrderSelectedId],
  );
  const handleDayTabChange = useCallback((_, value) => {
    // Reorder mode (and any in-progress pick) is scoped to a single day
    // tab. Leaving it active across tabs would let a job picked on one
    // day get swapped with a job on another, which doesn't make sense.
    setJobOrderToggle(false);
    setJobOrderSelectedId(null);
    setDayTab(value);
  }, []);

  const dayJobCounts = useMemo(() => {
    const counts = {
      yesterday: { total: 0, finished: 0 },
      today: { total: 0, finished: 0 },
      tomorrow: { total: 0, finished: 0 },
    };

    jobs.forEach((job) => {
      const sourceDate = job?.scheduled_start || job?.created_at;
      const bucket = toDateBucket(sourceDate);
      if (bucket in counts) {
        counts[bucket].total += 1;
        if (
          ["completed", "failed", "cancelled"].includes(toText(job?.status))
        ) {
          counts[bucket].finished += 1;
        }
      }
    });

    return counts;
  }, [jobs]);

  const selectedJob = useMemo(
    () =>
      jobs.find((job) => String(job.pk_id) === String(selectedJobId)) || null,
    [jobs, selectedJobId],
  );

  const selectedTasks = useMemo(() => {
    if (!selectedJob || !Array.isArray(selectedJob.tasks)) return [];
    return selectedJob.tasks;
  }, [selectedJob]);

  const selectedTaskProgress = useMemo(() => {
    const total = selectedTasks.length;
    if (total === 0) return { total: 0, done: 0 };

    const done = selectedTasks.filter((task) =>
      isTerminalTaskStatus(task?.status),
    ).length;

    return { total, done };
  }, [selectedTasks]);

  const selectedTaskBreakdown = useMemo(() => {
    const total = selectedTasks.length;
    const completed = selectedTasks.filter(
      (task) => normalizeTaskStatus(task?.status) === "completed",
    ).length;
    const failed = selectedTasks.filter(
      (task) => normalizeTaskStatus(task?.status) === "failed",
    ).length;
    const skippedOrNa = selectedTasks.filter((task) => {
      const status = normalizeTaskStatus(task?.status);
      return status === "skipped" || status === "not_applicable";
    }).length;

    const done = completed + failed + skippedOrNa;
    const pending = Math.max(0, total - done);

    return {
      total,
      completed,
      failed,
      skippedOrNa,
      pending,
      completedPct: total > 0 ? (completed / total) * 100 : 0,
      failedPct: total > 0 ? (failed / total) * 100 : 0,
      skippedOrNaPct: total > 0 ? (skippedOrNa / total) * 100 : 0,
    };
  }, [selectedTasks]);

  const selectedJobTotalMinutes = useMemo(() => {
    if (!selectedJob) return null;

    const startedAt = toUtcDate(selectedJob.actual_start);
    const endedAt = toUtcDate(selectedJob.actual_end);

    if (startedAt && endedAt) {
      const diffMs = endedAt.getTime() - startedAt.getTime();
      return Math.max(0, Math.round(diffMs / 60000));
    }

    if (
      startedAt &&
      ["in_progress", "in progress"].includes(toText(selectedJob.status))
    ) {
      return Math.max(0, Math.floor(jobElapsedSeconds / 60));
    }

    const summedTaskMinutes = selectedTasks.reduce((sum, task) => {
      const value = Number(task?.time_spent_minutes);
      return Number.isFinite(value) && value >= 0 ? sum + value : sum;
    }, 0);

    if (summedTaskMinutes > 0) return summedTaskMinutes;
    if (selectedTaskProgress.done > 0) return 0;
    return null;
  }, [
    jobElapsedSeconds,
    selectedJob,
    selectedTaskProgress.done,
    selectedTasks,
  ]);

  // Start/stop the job elapsed timer whenever the selected job or its status changes
  useEffect(() => {
    if (jobTimerRef.current) {
      clearInterval(jobTimerRef.current);
      jobTimerRef.current = null;
    }

    const jobStatus = toText(selectedJob?.status);
    const jobStartedAt = toUtcDate(selectedJob?.actual_start);

    if (jobStatus === "in_progress" && jobStartedAt) {
      const tick = () => {
        const diffMs = Date.now() - jobStartedAt.getTime();
        setJobElapsedSeconds(Math.max(0, Math.floor(diffMs / 1000)));
      };
      tick();
      jobTimerRef.current = setInterval(tick, 1000);
    } else {
      setJobElapsedSeconds(0);
    }

    return () => {
      if (jobTimerRef.current) {
        clearInterval(jobTimerRef.current);
        jobTimerRef.current = null;
      }
    };
  }, [selectedJob]);

  useEffect(() => {
    if (!selectedJob) {
      setTaskStatusFilter("todo");
      return;
    }

    setTaskStatusFilter(getDefaultTaskStatusFilter(selectedJob.status));
  }, [selectedJobId, selectedJob?.status]);

  useEffect(() => {
    if (!selectedJob || !Array.isArray(selectedJob.tasks)) {
      setTaskDrafts({});
      setExpandedTaskKeys({});
      setTaskFailureHighlightKeys({});
      setPendingFailureFocusTaskKey(null);
      setPanelError("");
      return;
    }

    const nextDrafts = {};
    selectedJob.tasks.forEach((task, index) => {
      const key = getTaskStableKey(task, index);
      nextDrafts[key] = {
        technician_notes: task?.technician_notes || "",
        failure_reason: task?.failure_reason || "",
        parts_used: formatPartsUsedForInput(task?.parts_used),
      };
    });

    setTaskDrafts(nextDrafts);
    setExpandedTaskKeys({});
    setTaskFailureHighlightKeys({});
    setPendingTaskStatusByKey({});
    setPendingFailureFocusTaskKey(null);
    setShowFailJobInline(false);
    setFailJobReason("");
    setFailJobReasonTouched(false);
    setPanelError("");
  }, [selectedJobId, selectedJob]);

  useEffect(() => {
    if (!pendingFailureFocusTaskKey) return;

    const focusAndScrollToInput = (input) => {
      if (!input || typeof input.focus !== "function") return false;

      input.focus({ preventScroll: true });

      const scrollHost = taskListScrollRef.current;
      if (
        scrollHost &&
        typeof scrollHost.scrollTo === "function" &&
        typeof input.getBoundingClientRect === "function"
      ) {
        const hostRect = scrollHost.getBoundingClientRect();
        const inputRect = input.getBoundingClientRect();
        const topWithinHost =
          inputRect.top - hostRect.top + scrollHost.scrollTop;
        const targetTop = Math.max(0, topWithinHost - hostRect.height * 0.28);
        scrollHost.scrollTo({ top: targetTop, behavior: "smooth" });
      } else if (typeof input.scrollIntoView === "function") {
        input.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }

      return true;
    };

    const targetInput = failureInputRefs.current[pendingFailureFocusTaskKey];
    if (focusAndScrollToInput(targetInput)) {
      setPendingFailureFocusTaskKey(null);
      return;
    }

    const retryId = window.setTimeout(() => {
      const retryInput = failureInputRefs.current[pendingFailureFocusTaskKey];
      if (focusAndScrollToInput(retryInput)) {
        setPendingFailureFocusTaskKey(null);
      }
    }, 60);

    return () => window.clearTimeout(retryId);
  }, [pendingFailureFocusTaskKey, expandedTaskKeys]);

  const patchJobInState = useCallback((jobPkId, updates, updatedJob = null) => {
    setJobs((prevJobs) =>
      prevJobs.map((row) => {
        if (String(row.pk_id) !== String(jobPkId)) {
          return row;
        }

        const tasks = Array.isArray(updatedJob?.tasks)
          ? updatedJob.tasks
          : Array.isArray(updates.tasks)
            ? updates.tasks
            : Array.isArray(row.tasks)
              ? row.tasks
              : [];

        return {
          ...row,
          ...updates,
          ...(updatedJob || {}),
          tasks,
          total_job_tasks: updatedJob?.total_job_tasks ?? tasks.length,
          updated_at: updatedJob?.updated_at || new Date().toISOString(),
        };
      }),
    );
  }, []);

  const handleStartJob = useCallback(async () => {
    if (!selectedJob || saving) return;

    const startedAt = selectedJob.actual_start || new Date().toISOString();

    setSaving(true);
    setPanelError("");
    try {
      const response = await updateJob(selectedJob.pk_id, {
        status: "in_progress",
        actual_start: startedAt,
        actual_end: null,
      });
      const updatedJob = response?.job || response?.data || response || null;
      patchJobInState(
        selectedJob.pk_id,
        {
          status: "in_progress",
          actual_start: startedAt,
          actual_end: null,
        },
        updatedJob,
      );
    } catch (err) {
      setPanelError(err?.message || "Failed to start job");
    } finally {
      setSaving(false);
    }
  }, [patchJobInState, saving, selectedJob]);

  const handleCompleteJob = useCallback(async () => {
    if (!selectedJob || saving) return;

    setSaving(true);
    setPanelError("");
    try {
      const response = await updateJob(selectedJob.pk_id, {
        status: "completed",
      });
      const updatedJob = response?.job || response?.data || response || null;
      patchJobInState(selectedJob.pk_id, { status: "completed" }, updatedJob);
      closeDrawerAfterTerminalUpdate();
    } catch (err) {
      setPanelError(err?.message || "Failed to complete job");
    } finally {
      setSaving(false);
    }
  }, [closeDrawerAfterTerminalUpdate, patchJobInState, saving, selectedJob]);

  // Marks every still-pending task as completed (preserving any notes/parts
  // already entered on them) and completes the job in one action, for the
  // "Complete job" button at the bottom of the task list.
  const handleCompleteAllTasksAndJob = useCallback(async () => {
    if (!selectedJob || saving) return;

    const currentTasks = Array.isArray(selectedJob.tasks)
      ? selectedJob.tasks
      : [];

    const nowIso = new Date().toISOString();
    const jobActualStart = selectedJob.actual_start || nowIso;

    // Chain each newly-completed task's start time off the latest already
    // finished task, same as the single-task completion flow, so times
    // stay sequential instead of all reading zero minutes.
    let chainStartedAt =
      currentTasks
        .filter(
          (task) => isTerminalTaskStatus(task?.status) && task?.completed_at,
        )
        .map((task) => task.completed_at)
        .sort()
        .at(-1) || jobActualStart;

    const updatedTasks = currentTasks.map((task) => {
      if (isTerminalTaskStatus(task?.status)) {
        // Already finished — leave its status, notes, and timing untouched.
        return task;
      }

      const startedAt = task.started_at || chainStartedAt;
      const completedAt = nowIso;
      chainStartedAt = completedAt;

      return {
        ...task,
        status: "completed",
        started_at: startedAt,
        completed_at: completedAt,
        time_spent_minutes: calculateTimeSpentMinutes(startedAt, completedAt),
      };
    });

    const updates = {
      tasks: updatedTasks,
      status: "completed",
      total_job_tasks: updatedTasks.length,
      actual_start: selectedJob.actual_start || jobActualStart,
      actual_end: nowIso,
    };

    setSaving(true);
    setPanelError("");
    try {
      const response = await updateJob(selectedJob.pk_id, updates);
      const updatedJob = response?.job || response?.data || response || null;
      patchJobInState(selectedJob.pk_id, updates, updatedJob);
      closeDrawerAfterTerminalUpdate();
    } catch (err) {
      setPanelError(err?.message || "Failed to complete job");
    } finally {
      setSaving(false);
    }
  }, [closeDrawerAfterTerminalUpdate, patchJobInState, saving, selectedJob]);

  const handleReopenJob = useCallback(async () => {
    if (!selectedJob || saving) return;

    // Send tasks back to pending and clear their timing, but leave every
    // note/finding a technician entered (technician_notes, failure_reason,
    // parts_used, etc.) untouched.
    const nextTasks = (
      Array.isArray(selectedJob.tasks) ? selectedJob.tasks : []
    ).map((task) => ({
      ...task,
      status: "pending",
      started_at: null,
      completed_at: null,
      time_spent_minutes: null,
    }));

    const updates = {
      status: "assigned",
      actual_start: null,
      actual_end: null,
      tasks: nextTasks,
    };

    setSaving(true);
    setPanelError("");
    try {
      const response = await updateJob(selectedJob.pk_id, updates);
      const updatedJob = response?.job || response?.data || response || null;
      patchJobInState(selectedJob.pk_id, updates, updatedJob);
    } catch (err) {
      setPanelError(err?.message || "Failed to reopen job");
    } finally {
      setSaving(false);
    }
  }, [patchJobInState, saving, selectedJob]);

  const handleToggleFailJobInline = useCallback(() => {
    if (!selectedJob || saving) return;
    setShowFailJobInline((prev) => {
      if (!prev) {
        setFailJobReason(selectedJob.cancellation_reason || "");
        setFailJobReasonTouched(false);
      }
      return !prev;
    });
  }, [saving, selectedJob]);

  const handleFailJob = useCallback(async () => {
    if (!selectedJob || saving) return;

    const reason = String(failJobReason || "").trim();
    if (!reason) {
      setFailJobReasonTouched(true);
      return;
    }

    const nowIso = new Date().toISOString();
    const nextTasks = (
      Array.isArray(selectedJob.tasks) ? selectedJob.tasks : []
    ).map((task) => {
      if (isTerminalTaskStatus(task?.status)) {
        return task;
      }

      const startedAt = task?.started_at || selectedJob.actual_start || nowIso;
      const completedAt = task?.completed_at || nowIso;

      return {
        ...task,
        status: "failed",
        failure_reason: AUTO_FAIL_PENDING_TASK_REASON,
        started_at: startedAt,
        completed_at: completedAt,
        time_spent_minutes: calculateTimeSpentMinutes(startedAt, completedAt),
      };
    });

    const updates = {
      status: "failed",
      cancellation_reason: reason,
      actual_start: selectedJob.actual_start || nowIso,
      actual_end: nowIso,
      tasks: nextTasks,
    };

    setSaving(true);
    setPanelError("");
    try {
      const response = await updateJob(selectedJob.pk_id, updates);
      const updatedJob = response?.job || response?.data || response || null;
      patchJobInState(selectedJob.pk_id, updates, updatedJob);
      closeDrawerAfterTerminalUpdate();
      setShowFailJobInline(false);
      setFailJobReason("");
      setFailJobReasonTouched(false);
    } catch (err) {
      setPanelError(err?.message || "Failed to fail job");
    } finally {
      setSaving(false);
    }
  }, [
    closeDrawerAfterTerminalUpdate,
    failJobReason,
    patchJobInState,
    saving,
    selectedJob,
  ]);

  const toggleTaskExpanded = useCallback((task, index) => {
    const key = getTaskStableKey(task, index);
    setExpandedTaskKeys((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const updateTaskDraft = useCallback((task, index, field, value) => {
    const key = getTaskStableKey(task, index);
    setTaskDrafts((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [field]: value,
      },
    }));

    if (field === "failure_reason" && String(value || "").trim()) {
      setTaskFailureHighlightKeys((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }, []);

  const handleTaskStatusChange = useCallback(
    async (taskIndex, nextStatus = null) => {
      if (!selectedJob || saving) return;

      const currentTasks = Array.isArray(selectedJob.tasks)
        ? selectedJob.tasks
        : [];
      const targetTask = currentTasks[taskIndex];
      if (!targetTask) return;
      const targetTaskKey = getTaskStableKey(targetTask, taskIndex);
      const targetDraft = taskDrafts[targetTaskKey] || {
        technician_notes: targetTask?.technician_notes || "",
        failure_reason: targetTask?.failure_reason || "",
      };
      const targetPendingStatus = pendingTaskStatusByKey[targetTaskKey] || null;
      const shouldInferFailedFromSaveNotes =
        !nextStatus &&
        !targetPendingStatus &&
        !isTerminalTaskStatus(targetTask?.status) &&
        Boolean(String(targetDraft.failure_reason || "").trim());
      const targetResolvedStatus =
        nextStatus ||
        targetPendingStatus ||
        (shouldInferFailedFromSaveNotes ? "failed" : null) ||
        normalizeTaskStatus(targetTask?.status);
      const missingFailureNote =
        shouldPromptFailureReason(targetResolvedStatus) &&
        !String(targetDraft.failure_reason || "").trim();

      if (missingFailureNote) {
        setExpandedTaskKeys((prev) => ({
          ...prev,
          [targetTaskKey]: true,
        }));
        setTaskFailureHighlightKeys((prev) => ({
          ...prev,
          [targetTaskKey]: true,
        }));
        setPendingTaskStatusByKey((prev) => ({
          ...prev,
          [targetTaskKey]: targetResolvedStatus,
        }));
        setPendingFailureFocusTaskKey(targetTaskKey);
        return;
      }

      const shouldAnimateCompletion =
        nextStatus &&
        isTerminalTaskStatus(targetResolvedStatus) &&
        !isTerminalTaskStatus(targetTask?.status);

      if (shouldAnimateCompletion) {
        setAnimatingTaskKeys((prev) => ({
          ...prev,
          [targetTaskKey]: true,
        }));
        await wait(1000);
      }

      const nowIso = new Date().toISOString();
      const jobActualStart = selectedJob.actual_start || nowIso;

      // Use the most recent completed_at from any already-finished task as this
      // task's started_at so back-to-back tasks share a seamless timeline.
      const lastCompletedAt =
        currentTasks
          .filter(
            (t, i) =>
              i !== taskIndex &&
              isTerminalTaskStatus(t?.status) &&
              t?.completed_at,
          )
          .map((t) => t.completed_at)
          .sort()
          .at(-1) || null;

      const updatedTasks = currentTasks.map((task, index) => {
        if (index !== taskIndex) return task;

        const draftKey = getTaskStableKey(task, index);
        const draft = taskDrafts[draftKey] || {};
        const draftPendingStatus = pendingTaskStatusByKey[draftKey] || null;
        const inferFailedFromSaveNotes =
          !nextStatus &&
          !draftPendingStatus &&
          !isTerminalTaskStatus(task.status) &&
          Boolean(String(draft.failure_reason || "").trim());
        const resolvedStatus =
          nextStatus ||
          draftPendingStatus ||
          (inferFailedFromSaveNotes ? "failed" : null) ||
          normalizeTaskStatus(task.status);
        const persistPartsUsed = ["in_progress", "completed"].includes(
          resolvedStatus,
        );

        let startedAt = task.started_at || null;
        let completedAt = task.completed_at || null;

        if (
          ["completed", "failed", "skipped", "not_applicable"].includes(
            resolvedStatus,
          )
        ) {
          // First finished task starts at the job actual_start. Each later
          // finished task starts at the latest completed task timestamp.
          startedAt = startedAt || lastCompletedAt || jobActualStart;
          // Only stamp completed_at the first time a task reaches a terminal
          // state.  Subsequent note-saves must NOT overwrite it.
          completedAt = completedAt || nowIso;
        }

        const timeSpentMinutes = calculateTimeSpentMinutes(
          startedAt,
          completedAt,
        );

        return {
          ...task,
          status: resolvedStatus,
          started_at: startedAt,
          completed_at: completedAt,
          technician_notes: draft.technician_notes || null,
          failure_reason:
            shouldPromptFailureReason(resolvedStatus) && draft.failure_reason
              ? draft.failure_reason
              : shouldPromptFailureReason(resolvedStatus)
                ? task.failure_reason || null
                : null,
          parts_used: persistPartsUsed
            ? parsePartsUsedInput(draft.parts_used)
            : null,
          time_spent_minutes: timeSpentMinutes,
        };
      });

      const allDone =
        updatedTasks.length > 0 &&
        updatedTasks.every((task) => isTerminalTaskStatus(task?.status));

      // When the first task is resolved the job is automatically in_progress.
      // When all tasks are done the job auto-completes. Terminal job states
      // (completed/failed/etc.) must be preserved when users edit notes.
      const nextJobStatus = resolveNextJobStatus(selectedJob.status, allDone);

      const updates = {
        tasks: updatedTasks,
        status: nextJobStatus,
        total_job_tasks: updatedTasks.length,
        actual_start:
          nextJobStatus === "in_progress" || nextJobStatus === "completed"
            ? selectedJob.actual_start || jobActualStart
            : selectedJob.actual_start || null,
        actual_end: allDone ? nowIso : selectedJob.actual_end || null,
      };

      setSaving(true);
      setPanelError("");
      try {
        const response = await updateJob(selectedJob.pk_id, updates);
        const updatedJob = response?.job || response?.data || response || null;
        patchJobInState(selectedJob.pk_id, updates, updatedJob);
        if (["completed", "failed"].includes(nextJobStatus)) {
          closeDrawerAfterTerminalUpdate();
        }
        setPendingTaskStatusByKey((prev) => {
          if (!prev[targetTaskKey]) return prev;
          const next = { ...prev };
          delete next[targetTaskKey];
          return next;
        });

        if (nextStatus && isTerminalTaskStatus(nextStatus)) {
          setExpandedTaskKeys((prev) => ({
            ...prev,
            [targetTaskKey]: true,
          }));
        }
      } catch (err) {
        setAnimatingTaskKeys((prev) => {
          const next = { ...prev };
          delete next[targetTaskKey];
          return next;
        });
        setPanelError(err?.message || "Failed to update task status");
      } finally {
        setAnimatingTaskKeys((prev) => {
          const next = { ...prev };
          delete next[targetTaskKey];
          return next;
        });
        setSaving(false);
      }
    },
    [
      closeDrawerAfterTerminalUpdate,
      patchJobInState,
      pendingTaskStatusByKey,
      saving,
      selectedJob,
      taskDrafts,
    ],
  );

  return (
    <>
      <TopNavBar
        title={t("pageTitle")}
        username={username}
        heading={t("heading")}
      />

      <Container sx={{ mt: 1.5, mb: 3, px: { xs: 1.25, sm: 2 } }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: "divider",
            mb: 1.5,
          }}
        >
          <CardContent sx={{ py: 1.5 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: "1.45rem" }}
              >
                {t("heading")}
              </Typography>
            </Stack>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 0.6, fontSize: "1rem" }}
            >
              {t("currentUser")}: {username}
            </Typography>
          </CardContent>
        </Card>

        <Tabs
          value={dayTab}
          onChange={handleDayTabChange}
          variant="fullWidth"
          sx={{ mb: 1.25, minHeight: 50 }}
        >
          <Tab
            value="yesterday"
            label={
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 0.2, sm: 0.75 }}
                alignItems="center"
              >
                <span>{t("tabs.yesterday")}</span>
                <Chip
                  size="medium"
                  label={`${dayJobCounts.yesterday.finished}/${dayJobCounts.yesterday.total}`}
                  variant="outlined"
                  sx={{ height: 24, fontSize: "0.8rem", px: 0.4 }}
                />
              </Stack>
            }
            sx={{ minHeight: 50, py: 0.9, fontSize: "1rem" }}
          />
          <Tab
            value="today"
            label={
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 0.2, sm: 0.75 }}
                alignItems="center"
              >
                <span>{t("tabs.today")}</span>
                <Chip
                  size="medium"
                  label={`${dayJobCounts.today.finished}/${dayJobCounts.today.total}`}
                  variant="outlined"
                  sx={{ height: 24, fontSize: "0.8rem", px: 0.4 }}
                />
              </Stack>
            }
            sx={{ minHeight: 50, py: 0.9, fontSize: "1rem" }}
          />
          <Tab
            value="tomorrow"
            label={
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 0.2, sm: 0.75 }}
                alignItems="center"
              >
                <span>{t("tabs.tomorrow")}</span>
                <Chip
                  size="medium"
                  label={`${dayJobCounts.tomorrow.finished}/${dayJobCounts.tomorrow.total}`}
                  variant="outlined"
                  sx={{ height: 24, fontSize: "0.8rem", px: 0.4 }}
                />
              </Stack>
            }
            sx={{ minHeight: 50, py: 0.9, fontSize: "1rem" }}
          />
        </Tabs>

        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={1}
          sx={{ mb: 1 }}
        >
          {jobOrderToggle ? (
            <Typography variant="body2" color="text.secondary">
              {jobOrderSelectedId
                ? t("jobOrdering.jobOrderPickSecond")
                : t("jobOrdering.jobOrderPickFirst")}
            </Typography>
          ) : null}
          <Button
            size="small"
            variant={jobOrderToggle ? "contained" : "outlined"}
            startIcon={<SwapVertRoundedIcon />}
            onClick={() => setJobOrderToggle((prev) => !prev)}
          >
            {jobOrderToggle
              ? t("jobOrdering.doneReording")
              : t("jobOrdering.reorderJobs")}
          </Button>
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        ) : null}

        {!loading && !error ? (
          <Stack spacing={1}>
            {dayFilteredJobs.length === 0 ? (
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="body1" color="text.secondary">
                    {t("noJobsForDay")}
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              dayFilteredJobs.map((job, index) => {
                const tasks = Array.isArray(job.tasks) ? job.tasks : [];
                const doneTasks = tasks.filter((task) =>
                  isTerminalTaskStatus(task?.status),
                ).length;
                const hasNewTask = tasks.some((task) => isNonRoutineTask(task));
                const isJobCompleted = toText(job?.status) === "completed";
                const isPickedForOrder =
                  jobOrderToggle &&
                  !isJobCompleted &&
                  jobOrderSelectedId === String(job.pk_id);
                const isOrderLocked = jobOrderToggle && isJobCompleted;

                return (
                  <Card
                    key={job.pk_id}
                    elevation={0}
                    onClick={
                      jobOrderToggle && !isJobCompleted
                        ? () => handleJobOrderSelect(job.pk_id)
                        : undefined
                    }
                    sx={{
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: isPickedForOrder
                        ? "primary.main"
                        : "divider",
                      boxShadow: isPickedForOrder
                        ? (theme) => `0 0 0 2px ${theme.palette.primary.main}`
                        : "none",
                      cursor:
                        jobOrderToggle && !isJobCompleted
                          ? "pointer"
                          : "default",
                      opacity: isOrderLocked ? 0.6 : 1,
                      transition:
                        "border-color 40ms ease, box-shadow 40ms ease",
                    }}
                  >
                    <CardContent sx={{ py: 1.35, px: 1.5 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={1}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack
                            direction="row"
                            spacing={0.8}
                            alignItems="center"
                          >
                            {jobOrderToggle && !isJobCompleted ? (
                              <DragIndicatorRoundedIcon
                                fontSize="small"
                                sx={{
                                  color: isPickedForOrder
                                    ? "primary.main"
                                    : "text.disabled",
                                }}
                              />
                            ) : null}
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 700,
                                fontSize: { xs: "2.05rem", sm: "2.6rem" },
                                lineHeight: 1,
                                letterSpacing: "-0.02em",
                                color: getRoomNameColor(job?.room_restriction),
                              }}
                              noWrap
                            >
                              {index + 1}. {job.room_name}
                            </Typography>
                            {isHighPriority(job?.priority) ? (
                              <Chip
                                size="small"
                                label={t("highPriority")}
                                color="error"
                                variant="outlined"
                                sx={{ height: 22, fontSize: "0.72rem" }}
                              />
                            ) : null}
                          </Stack>
                        </Box>
                        <Chip
                          size="medium"
                          label={job.status || "unknown"}
                          color={getStatusColor(job.status)}
                          sx={{
                            fontSize: "0.9rem",
                            height: 34,
                            textTransform: "lowercase",
                          }}
                        />
                      </Stack>

                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mt: 1.35 }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.9}
                        >
                          <RoomRestrictionBadge
                            restriction={job?.room_restriction}
                          />
                          <Typography
                            variant="subtitle1"
                            color={hasNewTask ? "error.main" : "text.secondary"}
                            sx={{
                              fontSize: { xs: "1.9rem", sm: "2.45rem" },
                              lineHeight: 1,
                              fontWeight: hasNewTask ? 700 : 500,
                            }}
                          >
                            {t("tasks")} {doneTasks}/{tasks.length}
                          </Typography>
                          {hasNewTask ? (
                            <Chip
                              size="small"
                              label={t("new")}
                              color="error"
                              sx={{ height: 22, fontSize: "0.72rem" }}
                            />
                          ) : null}
                        </Stack>
                      </Stack>

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-end"
                        spacing={1}
                        sx={{ mt: 0.8 }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="div"
                          sx={{ fontSize: "1.25rem" }}
                        >
                          {job.job_number}
                        </Typography>

                        <Button
                          size="large"
                          variant="outlined"
                          sx={{
                            fontSize: { xs: "1.45rem", sm: "2.2rem" },
                            px: { xs: 2.6, sm: 3.3 },
                            py: { xs: 0.85, sm: 1 },
                            minWidth: { xs: 138, sm: 168 },
                            borderRadius: 1.8,
                            letterSpacing: "0.02em",
                          }}
                          onClick={(event) => {
                            if (jobOrderToggle) {
                              // In reorder mode, clicks are for picking jobs
                              // to swap, not opening the details drawer.
                              event.stopPropagation();
                              handleJobOrderSelect(job.pk_id);
                              return;
                            }
                            setSelectedJobId(job.pk_id);
                            setPanelError("");
                            setDrawerOpen(true);
                          }}
                          disabled={jobOrderToggle && isJobCompleted}
                        >
                          {jobOrderToggle
                            ? t("jobOrdering.selectToSwap")
                            : t("openButton")}
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </Stack>
        ) : null}

        <Drawer
          anchor={isMobile ? "bottom" : "right"}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{
            disableRestoreFocus: true,
            disableAutoFocus: true,
          }}
          PaperProps={{
            sx: {
              width: isMobile ? "100%" : "60vw",
              height: isMobile ? "92vh" : "100%",
              borderTopLeftRadius: isMobile ? 16 : 0,
              borderTopRightRadius: isMobile ? 16 : 0,
              p: 1.25,
              overscrollBehaviorY: "contain",
              touchAction: "pan-y",
            },
          }}
        >
          {selectedJob ? (
            <Box
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              {isMobile ? (
                <Box
                  onTouchStart={handleDrawerPullStart}
                  onTouchMove={handleDrawerPullMove}
                  onTouchEnd={handleDrawerPullEnd}
                  onTouchCancel={handleDrawerPullEnd}
                  sx={{
                    width: 44,
                    height: 5,
                    borderRadius: 999,
                    bgcolor: "divider",
                    mx: "auto",
                    mt: 0.25,
                    mb: 0.75,
                  }}
                />
              ) : null}

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ px: 1, py: 0.5 }}
              >
                <Box>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <RoomRestrictionBadge
                      restriction={selectedJob?.room_restriction}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: getRoomNameColor(selectedJob?.room_restriction),
                      }}
                    >
                      {selectedJob.room_name}
                    </Typography>
                    {isHighPriority(selectedJob?.priority) ? (
                      <Chip
                        size="small"
                        label={t("highPriority")}
                        color="error"
                        variant="outlined"
                        sx={{ height: 18, fontSize: "0.65rem" }}
                      />
                    ) : null}
                  </Stack>

                  <Typography variant="caption" color="text.secondary">
                    {selectedJob.job_number || t("noJob")}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    paddingLeft={1.5}
                  >
                    {selectedJob.device_name ||
                      selectedJob.device_id ||
                      t("noDevice")}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  {toText(selectedJob.status) === "completed" ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleReopenJob}
                      disabled={saving}
                    >
                      {t("reopenJob") || "Reopen"}
                    </Button>
                  ) : null}
                  <Chip
                    size="small"
                    label={selectedJob.status || "unknown"}
                    color={getStatusColor(selectedJob.status)}
                  />
                </Stack>
              </Stack>

              {panelError ? (
                <Alert severity="warning" sx={{ mx: 1, mt: 0.5 }}>
                  {panelError}
                </Alert>
              ) : null}

              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ px: 1, pt: 1, pb: 1.2, flexWrap: "wrap", rowGap: 0.5 }}
              >
                {toText(selectedJob.status) === "in_progress" ? (
                  <Chip
                    size="medium"
                    label={`⏱ ${formatDuration(Math.floor(jobElapsedSeconds / 60))} ${t("elapsed")}`}
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: "0.9rem", height: 30 }}
                  />
                ) : (
                  <Button
                    size="medium"
                    variant="contained"
                    sx={{ fontSize: "0.95rem", px: 1.6 }}
                    onClick={handleStartJob}
                    disabled={
                      saving ||
                      [
                        "in_progress",
                        "in progress",
                        "completed",
                        "failed",
                      ].includes(toText(selectedJob.status))
                    }
                  >
                    {t("startJob")}
                  </Button>
                )}
                <Button
                  size="medium"
                  variant="outlined"
                  color="error"
                  sx={{ fontSize: "0.95rem", px: 1.6 }}
                  onClick={
                    showFailJobInline
                      ? handleFailJob
                      : handleToggleFailJobInline
                  }
                  disabled={
                    saving ||
                    ["completed", "failed", "cancelled"].includes(
                      toText(selectedJob.status),
                    )
                  }
                >
                  {showFailJobInline ? t("confirmFailJob") : t("failJob")}
                </Button>
                <Chip
                  size="medium"
                  label={`${selectedTaskProgress.done}/${selectedTaskProgress.total} ${t("done")}`}
                  variant="outlined"
                  sx={{ fontSize: "0.9rem", height: 30 }}
                />
                {["completed", "failed"].includes(
                  toText(selectedJob.status),
                ) ? (
                  <Chip
                    size="medium"
                    icon={<AccessTimeOutlinedIcon />}
                    label={
                      selectedJobTotalMinutes == null
                        ? "0 min"
                        : formatDuration(selectedJobTotalMinutes) || "0 min"
                    }
                    variant="outlined"
                    sx={{ fontSize: "0.9rem", height: 30 }}
                  />
                ) : null}
              </Stack>

              {selectedTaskBreakdown.total > 0 ? (
                <Box sx={{ px: 1, pb: 1.1 }}>
                  <Box
                    sx={{
                      height: 8,
                      borderRadius: 999,
                      overflow: "hidden",
                      bgcolor: "#e6ecf2",
                      display: "flex",
                    }}
                  >
                    {selectedTaskBreakdown.completedPct > 0 ? (
                      <Box
                        sx={{
                          width: `${selectedTaskBreakdown.completedPct}%`,
                          bgcolor: "success.main",
                          transition: "width 450ms ease",
                        }}
                      />
                    ) : null}
                    {selectedTaskBreakdown.failedPct > 0 ? (
                      <Box
                        sx={{
                          width: `${selectedTaskBreakdown.failedPct}%`,
                          bgcolor: "error.main",
                          transition: "width 450ms ease",
                        }}
                      />
                    ) : null}
                    {selectedTaskBreakdown.skippedOrNaPct > 0 ? (
                      <Box
                        sx={{
                          width: `${selectedTaskBreakdown.skippedOrNaPct}%`,
                          bgcolor: "info.main",
                          transition: "width 450ms ease",
                        }}
                      />
                    ) : null}
                  </Box>
                </Box>
              ) : null}

              <Divider />

              {showFailJobInline ? (
                <Box sx={{ px: 1, pt: 1, pb: 1 }}>
                  <TextField
                    label={t("jobFailureReason")}
                    placeholder={t("jobFailureReasonPlaceholder")}
                    value={failJobReason}
                    onChange={(event) => {
                      setFailJobReason(event.target.value);
                      setFailJobReasonTouched(true);
                    }}
                    error={
                      failJobReasonTouched &&
                      !String(failJobReason || "").trim()
                    }
                    helperText={
                      failJobReasonTouched &&
                      !String(failJobReason || "").trim()
                        ? t("jobFailureReasonHint")
                        : ""
                    }
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    autoFocus
                    sx={{
                      "& .MuiInputBase-input": { fontSize: "1rem" },
                      "& .MuiInputLabel-root": { fontSize: "1rem" },
                      "& .MuiFormHelperText-root": { fontSize: "0.9rem" },
                    }}
                  />
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    sx={{ mt: 0.75 }}
                  >
                    <Button
                      size="medium"
                      sx={{ fontSize: "0.95rem" }}
                      onClick={handleToggleFailJobInline}
                      disabled={saving}
                    >
                      {t("cancel")}
                    </Button>
                  </Stack>
                </Box>
              ) : null}

              <Tabs
                value={taskStatusFilter}
                onChange={(_, v) => setTaskStatusFilter(v)}
                variant="fullWidth"
                sx={{ minHeight: 40, borderBottom: 1, borderColor: "divider" }}
              >
                <Tab
                  value="todo"
                  label={t("taskFilter.todo")}
                  sx={{ minHeight: 40, py: 0.5, fontSize: "0.95rem" }}
                />
                <Tab
                  value="finished"
                  label={t("taskFilter.finished")}
                  sx={{ minHeight: 40, py: 0.5, fontSize: "0.95rem" }}
                />
                <Tab
                  value="all"
                  label={t("taskFilter.all")}
                  sx={{ minHeight: 40, py: 0.5, fontSize: "0.95rem" }}
                />
              </Tabs>

              <Box
                ref={taskListScrollRef}
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  overscrollBehaviorY: "contain",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                {selectedTasks.length === 0 ? (
                  <Box sx={{ p: 2 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "1rem" }}
                    >
                      {t("noTasksConfigured")}
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ px: 1 }}>
                    {selectedTasks
                      .filter((task) => {
                        const done = isTerminalTaskStatus(task?.status);
                        if (taskStatusFilter === "todo") return !done;
                        if (taskStatusFilter === "finished") return done;
                        return true;
                      })
                      .map((task, index) => {
                        // Keep original index so status-change callbacks still address the right task
                        const originalIndex = selectedTasks.indexOf(task);
                        const index_ =
                          originalIndex >= 0 ? originalIndex : index;
                        const taskStatus = normalizeTaskStatus(task?.status);
                        const stableKey = getTaskStableKey(task, index_);
                        const detailsOpen = Boolean(
                          expandedTaskKeys[stableKey],
                        );
                        const draft = taskDrafts[stableKey] || {
                          technician_notes: task?.technician_notes || "",
                          failure_reason: task?.failure_reason || "",
                          parts_used: formatPartsUsedForInput(task?.parts_used),
                        };
                        const isAnimatingOut = Boolean(
                          animatingTaskKeys[stableKey],
                        );
                        const showFailureHint =
                          shouldPromptFailureReason(taskStatus) &&
                          !String(draft.failure_reason || "").trim();
                        const highlightFailureField =
                          Boolean(taskFailureHighlightKeys[stableKey]) &&
                          shouldPromptFailureReason(taskStatus) &&
                          !String(draft.failure_reason || "").trim();
                        const isInProgress = taskStatus === "in_progress";
                        const isPending = taskStatus === "pending";
                        const isDone = isTerminalTaskStatus(taskStatus);

                        return (
                          <Box
                            key={`${selectedJob.pk_id}_${task.pk_id || index_}`}
                            sx={{
                              overflow: "hidden",
                              display: "grid",
                              gridTemplateRows: isAnimatingOut ? "0fr" : "1fr",
                              opacity: isAnimatingOut ? 0 : 1,
                              transition:
                                "grid-template-rows 1000ms ease, opacity 1000ms ease",
                            }}
                          >
                            <Box sx={{ minHeight: 0 }}>
                              <ListItem
                                sx={{
                                  px: 0.4,
                                  py: 1.2,
                                  alignItems: "flex-start",
                                  transform: isAnimatingOut
                                    ? "translateX(44px)"
                                    : "translateX(0)",
                                  opacity: isAnimatingOut ? 0 : 1,
                                  transition:
                                    "transform 1000ms ease, opacity 1000ms ease",
                                }}
                              >
                                <Box sx={{ width: "100%" }}>
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: "1.22rem",
                                      }}
                                    >
                                      {getTaskTitle(task, index, language)}
                                    </Typography>
                                    {isNonRoutineTask(task) ? (
                                      <Chip
                                        size="small"
                                        label={t("new")}
                                        color="error"
                                        sx={{ height: 22, fontSize: "0.78rem" }}
                                      />
                                    ) : null}
                                  </Stack>

                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.8}
                                    sx={{ mt: 0.35 }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      component="span"
                                      sx={{ fontSize: "1.05rem" }}
                                    >
                                      {formatTaskStatusLabel(taskStatus)}
                                    </Typography>
                                    {isAnimatingOut ? (
                                      <CheckCircleRoundedIcon
                                        color="primary"
                                        sx={{ fontSize: 18 }}
                                      />
                                    ) : null}
                                  </Stack>

                                  {SHOW_TASK_TIME_CHIPS && task?.started_at ? (
                                    <Stack
                                      direction="row"
                                      spacing={0.5}
                                      sx={{
                                        mt: 0.6,
                                        flexWrap: "wrap",
                                        rowGap: 0.4,
                                      }}
                                    >
                                      <Chip
                                        size="small"
                                        label={`▶ ${formatTimeLabel(task.started_at)}`}
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontSize: "0.7rem", height: 22 }}
                                      />
                                      {task.time_spent_minutes != null ? (
                                        <Chip
                                          size="small"
                                          label={`⏱ ${formatDuration(task.time_spent_minutes)}`}
                                          variant="outlined"
                                          sx={{
                                            fontSize: "0.7rem",
                                            height: 22,
                                          }}
                                        />
                                      ) : isInProgress ? (
                                        <Chip
                                          size="small"
                                          label="⏱ running…"
                                          color="primary"
                                          variant="outlined"
                                          sx={{
                                            fontSize: "0.7rem",
                                            height: 22,
                                          }}
                                        />
                                      ) : null}
                                      {task?.completed_at ? (
                                        <Chip
                                          size="small"
                                          label={`■ ${formatTimeLabel(task.completed_at)}`}
                                          color="success"
                                          variant="outlined"
                                          sx={{
                                            fontSize: "0.7rem",
                                            height: 22,
                                          }}
                                        />
                                      ) : null}
                                    </Stack>
                                  ) : null}

                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    spacing={1.2}
                                    sx={{ mt: 0.9 }}
                                  >
                                    <Button
                                      size="medium"
                                      variant="text"
                                      sx={{
                                        fontSize: "1.02rem",
                                        fontWeight: 600,
                                      }}
                                      onClick={() =>
                                        toggleTaskExpanded(task, index_)
                                      }
                                    >
                                      {detailsOpen
                                        ? t("hideDetails")
                                        : t("details")}
                                    </Button>

                                    {!isDone ? (
                                      <Stack
                                        direction="row"
                                        alignItems="flex-end"
                                        spacing={1}
                                      >
                                        <Button
                                          size="small"
                                          variant={
                                            taskStatus === "failed"
                                              ? "contained"
                                              : "outlined"
                                          }
                                          color="error"
                                          sx={{
                                            minWidth: 78,
                                            minHeight: 38,
                                            maxHeight: 38,
                                            manWidth: 78,
                                            fontWeight: 700,
                                            fontSize: "0.9rem",
                                          }}
                                          onClick={() =>
                                            handleTaskStatusChange(
                                              index_,
                                              "failed",
                                            )
                                          }
                                          disabled={
                                            saving || isDone || isAnimatingOut
                                          }
                                        >
                                          {t("fail")}
                                        </Button>
                                        <Button
                                          size="small"
                                          variant={
                                            taskStatus === "not_applicable"
                                              ? "contained"
                                              : "outlined"
                                          }
                                          sx={{
                                            minWidth: 78,
                                            minHeight: 38,
                                            maxHeight: 38,
                                            maxWidth: 78,
                                            fontWeight: 700,
                                            fontSize: "0.9rem",
                                          }}
                                          onClick={() =>
                                            handleTaskStatusChange(
                                              index_,
                                              "not_applicable",
                                            )
                                          }
                                          disabled={
                                            saving || isDone || isAnimatingOut
                                          }
                                        >
                                          {t("notApplicable")}
                                        </Button>
                                        <Button
                                          size="medium"
                                          variant={
                                            taskStatus === "completed"
                                              ? "contained"
                                              : "outlined"
                                          }
                                          sx={{
                                            minWidth: 142,
                                            height: 56,
                                            minHeight: 56,
                                            px: 2.6,
                                            py: 1.25,
                                            fontWeight: 700,
                                            fontSize: "1.08rem",
                                            lineHeight: 1,
                                          }}
                                          onClick={() =>
                                            handleTaskStatusChange(
                                              index_,
                                              "completed",
                                            )
                                          }
                                          disabled={
                                            saving || isDone || isAnimatingOut
                                          }
                                        >
                                          {t("complete")}
                                        </Button>
                                      </Stack>
                                    ) : null}
                                  </Stack>

                                  <Collapse
                                    in={detailsOpen}
                                    timeout="auto"
                                    unmountOnExit
                                  >
                                    <Stack spacing={1} sx={{ mt: 1 }}>
                                      {task?.task_description ||
                                      task?.task_description_dk ? (
                                        <Alert
                                          severity="info"
                                          sx={{
                                            py: 0.25,
                                            "& .MuiAlert-message": {
                                              fontSize: "1rem",
                                            },
                                          }}
                                        >
                                          {language === "da" &&
                                          task?.task_description_dk
                                            ? task.task_description_dk
                                            : task.task_description}
                                        </Alert>
                                      ) : null}

                                      {showFailureHint ? (
                                        <Alert
                                          severity={
                                            highlightFailureField
                                              ? "error"
                                              : "warning"
                                          }
                                          sx={{
                                            py: 0.25,
                                            "& .MuiAlert-message": {
                                              fontSize: "1rem",
                                            },
                                          }}
                                        >
                                          {t("taskFailureHint").replace(
                                            "{status}",
                                            formatTaskStatusLabel(taskStatus),
                                          )}
                                        </Alert>
                                      ) : null}

                                      <TextField
                                        label={t("technicianNotes")}
                                        value={draft.technician_notes}
                                        onChange={(event) =>
                                          updateTaskDraft(
                                            task,
                                            index,
                                            "technician_notes",
                                            event.target.value,
                                          )
                                        }
                                        fullWidth
                                        size="small"
                                        multiline
                                        minRows={2}
                                        sx={{
                                          "& .MuiInputBase-input": {
                                            fontSize: "1rem",
                                          },
                                          "& .MuiInputLabel-root": {
                                            fontSize: "1rem",
                                          },
                                        }}
                                      />

                                      <TextField
                                        label={t("failureNote")}
                                        placeholder={t(
                                          "failureNotePlaceholder",
                                        )}
                                        value={draft.failure_reason}
                                        inputRef={(element) => {
                                          if (element) {
                                            failureInputRefs.current[
                                              stableKey
                                            ] = element;
                                          } else {
                                            delete failureInputRefs.current[
                                              stableKey
                                            ];
                                          }
                                        }}
                                        error={highlightFailureField}
                                        helperText={
                                          highlightFailureField
                                            ? t("failureNoteRequired")
                                            : ""
                                        }
                                        onChange={(event) =>
                                          updateTaskDraft(
                                            task,
                                            index,
                                            "failure_reason",
                                            event.target.value,
                                          )
                                        }
                                        fullWidth
                                        size="small"
                                        multiline
                                        minRows={2}
                                        sx={{
                                          "& .MuiInputBase-input": {
                                            fontSize: "1rem",
                                          },
                                          "& .MuiInputLabel-root": {
                                            fontSize: "1rem",
                                          },
                                          "& .MuiFormHelperText-root": {
                                            fontSize: "0.9rem",
                                          },
                                        }}
                                      />

                                      {taskStatus === "completed" ||
                                      isInProgress ? (
                                        <TextField
                                          label={t("partsUsed")}
                                          placeholder={t(
                                            "partsUsedPlaceholder",
                                          )}
                                          value={draft.parts_used}
                                          onChange={(event) =>
                                            updateTaskDraft(
                                              task,
                                              index,
                                              "parts_used",
                                              event.target.value,
                                            )
                                          }
                                          fullWidth
                                          size="small"
                                          multiline
                                          minRows={2}
                                          sx={{
                                            "& .MuiInputBase-input": {
                                              fontSize: "1rem",
                                            },
                                            "& .MuiInputLabel-root": {
                                              fontSize: "1rem",
                                            },
                                          }}
                                        />
                                      ) : null}

                                      <Stack
                                        direction="row"
                                        justifyContent="flex-end"
                                      >
                                        <Button
                                          size="medium"
                                          variant="outlined"
                                          sx={{ fontSize: "0.95rem" }}
                                          onClick={() =>
                                            handleTaskStatusChange(index_, null)
                                          }
                                          disabled={saving}
                                        >
                                          {t("saveNotes")}
                                        </Button>
                                      </Stack>
                                    </Stack>
                                  </Collapse>
                                </Box>
                              </ListItem>
                              <Divider />
                            </Box>
                          </Box>
                        );
                      })}
                  </List>
                )}
              </Box>
              {selectedTasks.length > 0 &&
              !["completed", "failed", "cancelled"].includes(
                toText(selectedJob.status),
              ) ? (
                <Stack
                  direction="row"
                  justifyContent="center"
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    onClick={handleCompleteAllTasksAndJob}
                    disabled={saving}
                  >
                    {t("completeJobButton") || "Complete job"}
                  </Button>
                </Stack>
              ) : null}

              <Box sx={{ pb: isMobile ? 2 : 0.5 }} />
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t("selectJobPrompt")}
              </Typography>
            </Box>
          )}
        </Drawer>
      </Container>
    </>
  );
}
